
import EazyHeading from '@/components/EazyHeading.vue';
import requestAPI from '@/logic/requestBeApi';
import { loadStripe } from '@stripe/stripe-js';
import router from '@/router';
import getMeetingDateAndTime from '@/logic/helpers/dates/getMeetingDateAndTime';
import store from '@/store';
import { refreshTokenHandler } from '@/logic/auth';
import inputValidators from '@/logic/helpers/inputs/validators';
import config from '@/config';

let stripe: unknown;
let elements: unknown;
let card: unknown;

export default {
  name: 'ClientSubscription',

  components: {
    EazyHeading,
  },

  data(): Record<string, unknown> {
    return {
      stripe,
      elements,
      card,

      clientSubscription: {
        chosenSubscription: '',
      },
      validationRules: {
        chosenSubscription: [{ validator: inputValidators.primitive, trigger: 'change' }],
      },
      subscriptionsList: [],
      chosenPaymentMethod: {},
      existingPaymentMethods: [],
      plansFetching: false,
      existingPaymentMethodsFetching: false,
      processingPaymentMethod: false,
      processingCardDeleting: false,
      processingPlanDeleting: false,
      paymentMethodAdded: false,
      subscriptionPostSuccess: false,
      showAddNewCard: false,
      activePlan: undefined,
      router,
      getMeetingDateAndTime,
    };
  },

  computed: {
    canProceedToProfile(): boolean {
      return (
        this.existingPaymentMethods.length && (this.subscriptionPostSuccess || this.activePlan)
      );
    },

    isFullyOnboarded(): boolean {
      return store.state.fullyOnboarded;
    },

    canAddSubscription(): boolean {
      return this.isFullyOnboarded || this.$route.query.onboarding;
    },

    subscriptionStatus(): string {
      return store.state.subscriptionStatus;
    },

    chosenSubscription(): string {
      return this.clientSubscription.chosenSubscription;
    },
  },

  watch: {
    chosenSubscription(): void {
      this.subscriptionPostSuccess = false;
    },
  },

  created(): void {
    if (!this.canAddSubscription) return;

    this.getExistingPaymentMethods();
    this.getSubscriptionPlans();
    this.getSelectedPlans();
    this.initializeStripe();
  },

  methods: {
    async getSelectedPlans(): Promise<void> {
      this.plansFetching = true;

      const resp = await requestAPI({
        url: '/api/v1/subscriptions',
        method: 'GET',
      });

      if (resp.ok) {
        const data = await resp.json();
        this.activePlan = data[0];
        this.userAlreadyResignedFromHisPlan = !!this.activePlan?.cancel_at;
      } else if (resp.status !== 401) {
        this.$message.error(this.$t('someErrorOccurred'));
      }

      this.plansFetching = false;
    },

    async initializeStripe(): Promise<void> {
      this.stripe = await loadStripe(config.STRIPE_API_KEY);
      this.elements = this.stripe.elements();
      this.initStripeCard();
    },

    initStripeCard(): void {
      this.card = this.elements.create('card');
      this.card.mount(this.$refs.card);
    },

    async addPaymentMethodViaStripe(): Promise<void> {
      this.processingPaymentMethod = true;

      // 1 - Stripe BE
      const respStripeCreatePaymentMethod = await this.stripe.createPaymentMethod({
        type: 'card',
        card: this.card,
        billing_details: {
          name: localStorage.getItem('email'),
        },
      });

      const result = respStripeCreatePaymentMethod;
      if (result.error) {
        this.$forceUpdate();
        this.processingPaymentMethod = false;
        this.$message.error(this.$t('incorrectCardData'));
        return;
      }

      // 2 - our BE
      const payload = {
        payment_method_id: result.paymentMethod.id,
        type: 'card',
        data: result.paymentMethod,
      };

      const respOurBEAPICreatePaymentMethod = await requestAPI({
        url: '/api/v1/payment-methods',
        method: 'POST',
        payload,
      });

      if (respOurBEAPICreatePaymentMethod.ok) {
        this.$message.success(this.$t('cardAddedSuccess'));
        this.paymentMethodAdded = true;
        window.location.reload();
      } else if (respOurBEAPICreatePaymentMethod.status !== 401) {
        this.$message.error(this.$t('someErrorOccurred'));
      }

      this.processingPaymentMethod = false;
    },

    async postChosenSubPlan(): Promise<void> {
      this.processingPaymentMethod = true;

      const payload = {
        plan: `/api/v1/subscription-plans/${this.chosenSubscription}`,
        payment_method: `/api/v1/payment-methods/${this.chosenPaymentMethodOurBEId}`,
        // TODO:
        // once BE updated with handling trial on it own
        // please remove status from payload
        status: 'trial',
      };

      const resp = await requestAPI({
        url: '/api/v1/subscriptions',
        method: 'POST',
        payload,
      });

      if (resp.ok) {
        this.subscriptionPostSuccess = true;
        this.$message.success(this.$t('subPlanChosen'));

        const makeInitCall = true;
        const clearOldTokenTimeout = true;
        refreshTokenHandler(makeInitCall, clearOldTokenTimeout);
      } else if (resp.status !== 401) this.$message.error(this.$t('someErrorOccurred'));

      this.processingPaymentMethod = false;
    },

    async getSubscriptionPlans(): Promise<void> {
      this.plansFetching = true;

      const resp = await requestAPI({
        url: '/api/v1/subscription-plans',
        method: 'GET',
      });

      if (resp.ok) {
        const data = await resp.json();
        this.subscriptionsList = data;
      } else if (resp.status !== 401) {
        this.$message.error(this.$t('someErrorOccurred'));
      }

      this.plansFetching = false;
    },

    async getExistingPaymentMethods(): Promise<void> {
      this.existingPaymentMethodsFetching = true;

      const resp = await requestAPI({
        url: '/api/v1/payment-methods',
        method: 'GET',
      });

      if (resp.ok) {
        const data = await resp.json();
        this.existingPaymentMethods = data;
        if (data.length) {
          this.chosenPaymentMethodOurBEId = data[0].id;
          this.chosenPaymentMethod = data[0];
          this.paymentMethodAdded = true;
        }
      } else if (resp.status !== 401) {
        this.$message.error(this.$t('someErrorOccurred'));
      }

      this.existingPaymentMethodsFetching = false;
    },

    deletePlan(planId: string, immediateConfirmation: boolean): void {
      const delPlan = async () => {
        this.processingPlanDeleting = true;

        const resp = await requestAPI({
          url: `/api/v1/subscriptions/${planId}/cancel`,
          method: 'PATCH',
          contentType: 'application/merge-patch+json',
        });

        if (resp.ok) {
          window.location.reload();
        } else if (resp.status !== 401) {
          this.$message.error(this.$t('someErrorOccurred'));
        }

        this.processingPlanDeleting = false;
      };

      if (!immediateConfirmation) {
        this.$confirm(this.$t('subPlanSureResign'), this.$t('info'), {
          confirmButtonText: 'OK',
          cancelButtonText: this.$t('cancel'),
          type: 'info',
        })
          .then(async () => {
            delPlan();
          })
          .catch((err: Error) => err);
      } else {
        delPlan();
      }
    },

    proceedWithDeletion(cardId: string): void {
      this.$confirm(this.$t('cardSureResign'), this.$t('info'), {
        confirmButtonText: 'OK',
        cancelButtonText: this.$t('cancel'),
        type: 'info',
      })
        .then(async () => {
          this.processingCardDeleting = true;

          const resp = await requestAPI({
            url: `/api/v1/payment-methods/${cardId}`,
            method: 'DELETE',
          });

          if (resp.ok) {
            this.$message.success(this.$t('yourCardDel'));
            setTimeout(function() {
              window.location.reload();
            }, 1250);
          } else if (resp.status !== 401) {
            this.$message.error(this.$t('someErrorOccurred'));
          }

          this.processingCardDeleting = false;
        })
        .catch((err: Error) => err);
    },

    proceedWithNotAbleToDelNowInfo(): void {
      this.$message.info(
        `${this.$t('waitToDelYourCard')} ${this.getMeetingDateAndTime(this.activePlan.cancel_at)}`,
      );
    },

    deleteCard(cardId: string): void {
      const canDeleteCreditCard = !this.activePlan;

      if (canDeleteCreditCard) {
        this.proceedWithDeletion(cardId);
      } else {
        this.proceedWithNotAbleToDelNowInfo();
      }
    },

    getSubscriptionLabel(subscription: {
      amount: number;
      name: unknown;
      currency: unknown;
      interval: unknown;
    }): string {
      const amount = subscription.amount / 100;
      return `${subscription.name}: ${amount} ${subscription.currency}/${subscription.interval}`;
    },
  },
};
